import { useEffect } from 'react';

import { useQuery } from '@apollo/client';
import * as Sentry from '@sentry/react';
import moment from 'moment';

import type { InjectedRouter } from 'js/lib/connectToRouter';

import QueryOnboardingResponseByUserId from 'bundles/enterprise-learner-onboarding/queries/QueryOnboardingResponseByUserId.graphql';
import type {
  QueryOnboardingResponseByUserIdQuery,
  QueryOnboardingResponseByUserIdQueryVariables,
} from 'bundles/enterprise-learner-onboarding/queries/__generated__/QueryOnboardingResponseByUserId';
import { handleRedirect } from 'bundles/enterprise-learner-onboarding/utils';

type ShowLearnerOnboardingProps = {
  router: InjectedRouter;
  userId: number;
  programSlug?: string;
  isProgramMember?: boolean;
  isInvited?: boolean;
  hasEnabledPersonalizedRecommendations?: boolean;
  isInDeepBrowseMode?: boolean;
  isProductFeaturesLoading?: boolean;
  isShowingProgramAccessModal?: boolean;
};

const useShowLearnerOnboarding = ({
  router,
  userId,
  programSlug,
  isProgramMember,
  isInvited,
  hasEnabledPersonalizedRecommendations,
  isInDeepBrowseMode,
  isProductFeaturesLoading,
  isShowingProgramAccessModal,
}: ShowLearnerOnboardingProps) => {
  const {
    data: userOnboardingData,
    loading: userOnboardingDataLoading,
    error,
  } = useQuery<QueryOnboardingResponseByUserIdQuery, QueryOnboardingResponseByUserIdQueryVariables>(
    QueryOnboardingResponseByUserId,
    {
      variables: { userId: String(userId) },
      context: { clientName: 'gatewayGql' },
      fetchPolicy: 'network-only',
      ssr: false,
    }
  );
  useEffect(() => {
    if (error) {
      Sentry.captureException(error, {
        extra: { message: error.message },
      });
      return;
    }
    const hasCompletedOnboarding = userOnboardingData?.LearnerOnboarding?.queryResponseByUserId?.isCompleted ?? true;
    const onboardingRemindTime = userOnboardingData?.LearnerOnboarding?.queryResponseByUserId.remindMeByTime;
    const shouldRemindOnboarding = !onboardingRemindTime || moment(onboardingRemindTime).diff(Date.now(), 'days') < 0;

    if (
      (isProgramMember || isInvited) &&
      !isShowingProgramAccessModal &&
      !userOnboardingDataLoading &&
      !isProductFeaturesLoading &&
      !hasCompletedOnboarding &&
      shouldRemindOnboarding &&
      hasEnabledPersonalizedRecommendations &&
      isInDeepBrowseMode
    ) {
      handleRedirect(`/programs/${programSlug}/onboarding`, router);
    }
  }, [
    isProgramMember,
    isInvited,
    router,
    programSlug,
    hasEnabledPersonalizedRecommendations,
    isInDeepBrowseMode,
    userOnboardingData,
    userOnboardingDataLoading,
    isProductFeaturesLoading,
    isShowingProgramAccessModal,
    error,
  ]);
};

export default useShowLearnerOnboarding;
